import { estAFaire } from '../../domain/menage/aFaire';
import { pendingPreparations } from '../../domain/menage/preparations';
import type {
    CleaningType,
    HousekeepingRoomStatus,
    RoomStatus,
} from '../../pages/housekeeping/types/housekeeping';
import { t } from '../i18n/langue';

/**
 * Les règles d'affichage de « Mes chambres » — lot 3 Odomo.
 *
 * Les sections reprennent exactement `MyRooms.tsx` du web : une chambre déjà faite
 * mais avec une préparation arrivée ensuite est « à préparer », pas rangée dans
 * « Autres ». La règle des préparations elle-même vient de `domain/menage/`.
 *
 * Les libellés viennent du dictionnaire mobile (`i18n/fr/menage.json`) : ils
 * recopient `constants/statusConfig.ts`, que la frontière interdit d'importer en valeur.
 */

export interface ChambresReparties {
    enCours: HousekeepingRoomStatus[];
    aFaire: HousekeepingRoomStatus[];
    aPreparer: HousekeepingRoomStatus[];
    autres: HousekeepingRoomStatus[];
}

export function repartirChambres(chambres: HousekeepingRoomStatus[]): ChambresReparties {
    const enCours = chambres
        .filter((c) => c.status === 'cleaning')
        .sort((a, b) => (b.cleaning_started_at ?? '').localeCompare(a.cleaning_started_at ?? ''));
    const aFaire = chambres.filter(estAFaire);
    const faites = chambres.filter((c) => c.status !== 'cleaning' && !estAFaire(c));

    return {
        enCours,
        aFaire,
        aPreparer: faites.filter((c) => pendingPreparations(c).length > 0),
        autres: faites.filter((c) => pendingPreparations(c).length === 0),
    };
}

const TYPES: CleaningType[] = ['recouche', 'a_blanc', 'inspection', 'maintenance', 'special'];

/** `AGENT_CLEANING_TYPES` du web : un agent ne choisit qu'entre ces deux-là. */
const TYPES_AGENT: CleaningType[] = ['recouche', 'a_blanc'];

export function libelleType(type: CleaningType | null): string | null {
    return type ? t(`menage.types.${type}`) : null;
}

/** Les types offerts au démarrage : tous à qui affecte les chambres, deux à l'agent. */
export function typesProposes(peutAffecter: boolean): CleaningType[] {
    return peutAffecter ? TYPES : TYPES_AGENT;
}

export function libelleStatut(statut: RoomStatus): string {
    return t(`menage.statuts.${statut}`);
}

export function emplacementChambre(chambre: Pick<HousekeepingRoomStatus, 'floor' | 'building'>): string {
    return [chambre.floor ? t('menage.etage', { etage: chambre.floor }) : null, chambre.building].filter(Boolean).join(' · ');
}

/** Le type du PMS est-il inconnu ? Le web bloque alors le démarrage d'un agent. */
export function typeIndetermine(chambre: Pick<HousekeepingRoomStatus, 'cleaning_type' | 'cleaning_type_source'>): boolean {
    return chambre.cleaning_type === null && chambre.cleaning_type_source !== 'manual';
}
